import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "./useAuth";

export interface AlertPref {
  alertType: string;
  enabled: boolean;
  watchZipCodes?: string[];
  watchCounty?: string;
  watchCounties?: string[];
}

/** Stored counties the server cleared on save because the plan no longer
 *  allows them (PUT /alerts/preferences `cleared`). */
export interface ClearedAlertGeo {
  alertType: string;
  watchCounty?: string;
  watchCounties?: string[];
}

/**
 * Drop the values the server cleared from local state. Without this the UI
 * keeps the stale value and sends it on the next save, where the server, now
 * seeing a county that is not stored, refuses it as a new one.
 */
export function applyCleared(prefs: AlertPref[], cleared: ClearedAlertGeo[] | undefined): AlertPref[] {
  if (!cleared || cleared.length === 0) return prefs;
  const byType = new Map(cleared.map((c) => [c.alertType, c]));
  return prefs.map((p) => {
    const c = byType.get(p.alertType);
    if (!c) return p;
    const next = { ...p };
    if (c.watchCounty && next.watchCounty === c.watchCounty) next.watchCounty = "";
    if (c.watchCounties && c.watchCounties.length > 0 && next.watchCounties) {
      const drop = new Set(c.watchCounties);
      next.watchCounties = next.watchCounties.filter((wc) => !drop.has(wc));
    }
    return next;
  });
}

const ALL_ALERT_TYPES: AlertPref[] = [
  { alertType: "new_upset_bid", enabled: true },
  { alertType: "auction_reminder_7d", enabled: true },
  { alertType: "auction_reminder_1d", enabled: true },
  // Generic "any new foreclosure in my area" — fires for every pipeline
  // stage (notice_filed, auction_scheduled, notice_of_default)
  { alertType: "new_listing", enabled: false, watchZipCodes: [], watchCounties: [] },
  // Specialized "new NOTICE OF DEFAULT filing" — earliest pipeline stage,
  // maximum lead time before auction
  { alertType: "nod_new_filing", enabled: false, watchZipCodes: [], watchCounties: [] },
  { alertType: "status_change", enabled: false },
  // Earliest possible signal (#933): a published legal notice we haven't
  // matched to a property yet — no confirmed address, just county + sale
  // date + case number. Lower precision than new_listing/nod_new_filing
  // (which only fire once a property record exists), so defaults off same
  // as the other opt-in area alerts.
  { alertType: "new_notice_lead", enabled: false, watchZipCodes: [], watchCounties: [] },
];

export function useAlertPreferences() {
  const { user } = useAuth();
  // Depend on user.id, not the user object. Supabase's TOKEN_REFRESHED
  // on tab-return produces a new user object but the same id, so
  // depending on the object would re-run this effect every tab focus.
  const userId = user?.id;
  const [prefs, setPrefs] = useState<AlertPref[]>(ALL_ALERT_TYPES);
  const [loading, setLoading] = useState(false);
  // A failed load must not leave the hard-coded defaults on screen looking
  // like the user's saved settings: the next toggle would PUT them over the
  // real ones. loadError lets the UI refuse to render toggles until a retry
  // succeeds.
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // #505: when a save is rejected for exceeding the plan's whole-county alert
  // cap, surface it so the UI can show an upgrade nudge — and revert the
  // over-cap selection rather than leaving it checked until a reload wipes it.
  const [capError, setCapError] = useState<{ message: string; upgradeUrl?: string } | null>(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setLoadError(false);
    apiClient
      .get<{ data: AlertPref[] }>("/alerts/preferences")
      .then(({ data }) => {
        // Merge backend state with the default list to show every toggle
        const byType = new Map(data.data.map((p) => [p.alertType, p]));
        setPrefs(ALL_ALERT_TYPES.map((d) => byType.get(d.alertType) || d));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [userId, reloadTick]);

  const save = useCallback(async (next: AlertPref[]) => {
    const prev = prefsRef.current;
    setPrefs(next);
    setCapError(null);
    setSaveError(null);
    try {
      const res = await apiClient.put<{ cleared?: ClearedAlertGeo[] }>("/alerts/preferences", { prefs: next });
      const cleared = res?.data?.cleared;
      if (cleared && cleared.length > 0) {
        setPrefs((cur) => applyCleared(cur, cleared));
      }
    } catch (e: any) {
      // A plan-geography refusal (422: a county or zip outside the plan, the
      // free zip limit): nothing was stored, so revert to what the server
      // has and surface its message with the upgrade link. The server's
      // codes are alert_county_not_allowed, alert_county_not_selected,
      // alert_zip_limit and alert_zip_not_in_chosen_counties; any 422 with a
      // message is shown the same way.
      if (e?.response?.status === 422) {
        const data = e.response.data || {};
        setPrefs(prev);
        setCapError({
          message: data.message || "That alert area isn't included in your plan.",
          upgradeUrl: data.upgrade_url,
        });
        return;
      }
      // Anything else (network, 5xx): nothing was stored. Revert the optimistic
      // toggle so the screen matches the server, and say so instead of
      // leaving a switch on that was never saved.
      setPrefs(prev);
      setSaveError("We couldn't save that change. Check your connection and try again.");
    }
  }, []);

  return {
    prefs,
    save,
    loading,
    capError,
    clearCapError: () => setCapError(null),
    loadError,
    saveError,
    reload: () => setReloadTick((n) => n + 1),
  };
}
